import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Plus, Users, Settings, LogOut, Bell, Mail, Download, Share2 } from 'lucide-react';
import { Board, BoardInvitation } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { exportBoardToCSV } from '../utils/csvExport';


interface BoardSelectorProps {
  boards: Board[];
  currentBoard: Board | null;
  invitations: BoardInvitation[];
  products: any[];
  onBoardSelect: (board: Board) => void;
  onCreateBoard: () => void;
  onInviteUsers: () => void;
  onBoardSettings: () => void;
  onViewInvitations: () => void;
  onViewPendingInvitations: () => void;
  onShareBoard: () => void;
}

const BoardSelector: React.FC<BoardSelectorProps> = ({
  boards,
  currentBoard,
  invitations,
  products,
  onBoardSelect,
  onCreateBoard,
  onInviteUsers,
  onBoardSettings,
  onViewInvitations,
  onViewPendingInvitations,
  onShareBoard,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();

  const canInvite = currentBoard?.members.find(m => m.userId === user?.id)?.role === 'owner' || 
                   currentBoard?.members.find(m => m.userId === user?.id)?.role === 'admin' ||
                   currentBoard?.members.find(m => m.userId === user?.id)?.role === 'editor';

  const canEdit = currentBoard?.members.find(m => m.userId === user?.id)?.role === 'owner' || 
                 currentBoard?.members.find(m => m.userId === user?.id)?.role === 'admin' ||
                 currentBoard?.members.find(m => m.userId === user?.id)?.role === 'editor';

  const pendingInvitationsCount = invitations.length;

  return (
    <div className="relative">
      {/* Board Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-4 py-2 glass-effect rounded-xl hover:bg-white/30 transition-all duration-200"
      >
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-satsuma-500 to-heian-500" />
          <span className="font-medium text-gray-800">
            {currentBoard?.name || 'Select Board'}
          </span>
          {pendingInvitationsCount > 0 && (
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-satsuma-500 rounded-full animate-pulse" />
              <span className="text-xs text-satsuma-600 font-medium">
                {pendingInvitationsCount} invitation{pendingInvitationsCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
        <ChevronDown 
          size={16} 
          className={`text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl p-2 z-[9999] min-w-[280px] border border-gray-200 shadow-lg"
          >
            {/* Current User Info */}
            <div className="px-3 py-2 border-b border-gray-200 mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-satsuma-500 to-heian-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-600 truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Board List */}
            <div className="max-h-48 overflow-y-auto">
              {boards.length === 0 ? (
                <div className="px-3 py-4 text-center text-gray-500 text-sm">
                  No boards available
                </div>
              ) : (
                boards.map((board) => (
                  <button
                    key={board.id}
                    onClick={() => {
                      onBoardSelect(board);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                      currentBoard?.id === board.id
                        ? 'bg-satsuma-100 text-satsuma-700'
                        : 'hover:bg-white/30 text-gray-700'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      currentBoard?.id === board.id
                        ? 'bg-satsuma-500'
                        : 'bg-gray-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{board.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {board.members.length} member{board.members.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-white/20 pt-2 mt-2 space-y-1">
              {pendingInvitationsCount > 0 && (
                <button
                  onClick={() => {
                    onViewInvitations();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
                >
                  <Bell size={16} />
                  <span className="text-sm">
                    View Invitations ({pendingInvitationsCount})
                  </span>
                </button>
              )}
              
              <button
                onClick={() => {
                  onCreateBoard();
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
              >
                <Plus size={16} />
                <span className="text-sm">Create New Board</span>
              </button>

              {currentBoard && canInvite && (
                <>
                  <button
                    onClick={() => {
                      onInviteUsers();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
                  >
                    <Users size={16} />
                    <span className="text-sm">Invite Users</span>
                  </button>
                  <button
                    onClick={() => {
                      onViewPendingInvitations();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
                  >
                    <Mail size={16} />
                    <span className="text-sm">View Pending Invitations</span>
                  </button>
                </>
              )}

              {currentBoard && canEdit && (
                <>
                  <button
                    onClick={() => {
                      onBoardSettings();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
                  >
                    <Settings size={16} />
                    <span className="text-sm">Board Settings</span>
                  </button>
                  <button
                    onClick={() => {
                      if (currentBoard && products.length > 0) {
                        exportBoardToCSV(currentBoard, products);
                      }
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
                  >
                    <Download size={16} />
                    <span className="text-sm">Export to CSV</span>
                  </button>
                </>
              )}

              {currentBoard && canInvite && (
                <button
                  onClick={() => {
                    onShareBoard();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-gray-700"
                >
                  <Share2 size={16} />
                  <span className="text-sm">Share Board</span>
                </button>
              )}

              <button
                onClick={() => {
                  logout();
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-red-50 transition-all duration-200 text-red-600"
              >
                <LogOut size={16} />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


    </div>
  );
};

export default BoardSelector;
